import { useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import { useAuth } from '@/contexts/AuthContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { getInitials } from '@/types/auth';
import type { ChangePasswordRequest, UserDto } from '@/types/auth';

function buildSchema(t: (key: string) => string) {
  return z
    .object({
      currentPassword: z.string().min(1, t('changePasswordForm.validation.currentPasswordRequired')),
      newPassword: z.string().min(8, t('changePasswordForm.validation.newPasswordMinLength')),
      confirmPassword: z.string(),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('changePasswordForm.validation.confirmPasswordMismatch'),
      path: ['confirmPassword'],
    });
}

type ChangePasswordFormValues = z.infer<ReturnType<typeof buildSchema>>;

export default function MyProfilePage() {
  const { t } = useTranslation('profile');
  const { user, updateUser } = useAuth();
  const { formatDateTime } = useDateFormat();

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [photoMenuAnchor, setPhotoMenuAnchor] = useState<HTMLElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const captureInputRef = useRef<HTMLInputElement>(null);

  const avatarMutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiClient.post<UserDto>('/auth/me/avatar', formData);
    },
    onSuccess: (response) => updateUser(response.data),
  });

  const handlePhotoSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) avatarMutation.mutate(file);
    e.target.value = '';
  };

  const schema = buildSchema(t);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const changePasswordMutation = useMutation({
    mutationFn: (payload: ChangePasswordRequest) => apiClient.post('/auth/change-password', payload),
    onSuccess: () => reset(),
  });

  const onSubmit = (values: ChangePasswordFormValues) => {
    changePasswordMutation.mutate({ currentPassword: values.currentPassword, newPassword: values.newPassword });
  };

  const na = t('notAvailable');

  return (
    <>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      <GridContainer spacing={3}>
        <GridItem xs={12} md={5}>
          <Card>
            <CardContent>
              <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <Box sx={{ position: 'relative', width: 72, height: 72 }}>
                  <Avatar
                    src={user?.avatarUrl ?? undefined}
                    sx={{ width: 72, height: 72, bgcolor: user?.avatarColor, fontSize: 28 }}
                  >
                    {user ? getInitials(user) : undefined}
                  </Avatar>
                  <IconButton
                    size="small"
                    onClick={(e) => setPhotoMenuAnchor(e.currentTarget)}
                    disabled={avatarMutation.isPending}
                    sx={{
                      position: 'absolute',
                      bottom: -4,
                      right: -4,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { bgcolor: 'primary.dark' },
                      width: 28,
                      height: 28,
                    }}
                  >
                    <CameraAltOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Menu anchorEl={photoMenuAnchor} open={Boolean(photoMenuAnchor)} onClose={() => setPhotoMenuAnchor(null)}>
                    <MenuItem
                      onClick={() => {
                        setPhotoMenuAnchor(null);
                        captureInputRef.current?.click();
                      }}
                    >
                      <ListItemIcon>
                        <PhotoCameraOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                      {t('photo.takePhoto')}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setPhotoMenuAnchor(null);
                        uploadInputRef.current?.click();
                      }}
                    >
                      <ListItemIcon>
                        <UploadOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                      {t('photo.uploadPhoto')}
                    </MenuItem>
                  </Menu>
                  <input
                    ref={captureInputRef}
                    type="file"
                    accept="image/*"
                    capture="user"
                    hidden
                    onChange={handlePhotoSelected}
                  />
                  <input ref={uploadInputRef} type="file" accept="image/*" hidden onChange={handlePhotoSelected} />
                </Box>
                <Stack alignItems="center" spacing={0.25}>
                  <Typography variant="h5" fontWeight={700}>
                    {user?.fullName}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center">
                    {user?.roles.map((role) => (
                      <Chip key={role} label={role} size="small" color="primary" variant="outlined" />
                    ))}
                  </Stack>
                </Stack>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1.75}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('fields.username')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {user?.username}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('fields.email')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {user?.email}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('fields.company')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {user?.companyName ?? na}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('fields.branch')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {user?.branchName ?? na}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('fields.department')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {user?.departmentName ?? na}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('fields.lastLogin')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {user?.lastLoginDate ? formatDateTime(user.lastLoginDate) : na}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </GridItem>

        <GridItem xs={12} md={7}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <LockOutlinedIcon fontSize="small" color="primary" />
                <Typography variant="h5">{t('sections.changePassword')}</Typography>
              </Stack>

              <Box component="form" onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
                <Stack spacing={2.5} sx={{ maxWidth: 420 }}>
                  <Controller
                    name="currentPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type={showCurrent ? 'text' : 'password'}
                        label={t('changePasswordForm.currentPassword')}
                        error={Boolean(errors.currentPassword)}
                        helperText={errors.currentPassword?.message}
                        fullWidth
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockOutlinedIcon fontSize="small" color="action" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowCurrent((v) => !v)} edge="end" size="small">
                                  {showCurrent ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="newPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type={showNew ? 'text' : 'password'}
                        label={t('changePasswordForm.newPassword')}
                        error={Boolean(errors.newPassword)}
                        helperText={errors.newPassword?.message}
                        fullWidth
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockOutlinedIcon fontSize="small" color="action" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowNew((v) => !v)} edge="end" size="small">
                                  {showNew ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type={showNew ? 'text' : 'password'}
                        label={t('changePasswordForm.confirmPassword')}
                        error={Boolean(errors.confirmPassword)}
                        helperText={errors.confirmPassword?.message}
                        fullWidth
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockOutlinedIcon fontSize="small" color="action" />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || changePasswordMutation.isPending}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {t('changePasswordForm.submit')}
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </GridItem>
      </GridContainer>
    </>
  );
}
